import { inject } from 'inversify';
import { EntityId, EntityKey, Nullable } from '@Models';
import { CACHE_NAME } from '@Worker/CacheManager';
import { TYPES } from '@Types';
import { Locale } from './locale';
import { IMapper } from '@Mapper/IMapper';
import { entity_last_update } from './entity_last_update';

export default abstract class CacheRepository<
  K extends EntityKey,
  A extends EntityId<EntityKey>,
  E extends entity_last_update,
  O extends EntityId<EntityKey>,
> {
  protected key: string;
  @inject(TYPES.Locale) protected _locale!: Locale;

  protected constructor({ key }: { key: string }) {
    this.key = key;
  }

  private getCache(): Promise<Cache> {
    return window.caches.open(CACHE_NAME);
  }

  public async hasCachedEntity({
    dataId,
    locale,
  }: {
    dataId: EntityKey;
    locale: string | null;
  }): Promise<Boolean> {
    try {
      const cache = await this.getCache();
      const response = await cache.match(
        this.getKeyEntitity({ data: dataId, locale: locale }),
      );
      return response != null;
    } catch (err) {
      console.log('error-has-cache', err);
    }
    return false;
  }

  public async getUpdatedAt({
    dataId,
    locale,
  }: {
    dataId: EntityKey;
    locale: string | null;
  }): Promise<Nullable<number>> {
    try {
      const cache = await this.getCache();
      const response = await cache.match(
        this.getKeyVersioning({ data: dataId, locale: locale }),
      );
      if (!response) return null;

      return (JSON.parse(await response.text()) as number) ?? null;
    } catch (err) {
      console.log('error-load-cache', err);
    }
    return null;
  }

  public async getCached(
    id: EntityKey,
    { locale }: { locale: string | null },
  ): Promise<Nullable<O>> {
    return await this.getCachedEntity({ dataId: id, locale: locale });
  }

  public async getCachedEntity({
    dataId,
    locale,
  }: {
    dataId: EntityKey;
    locale: string | null;
  }): Promise<Nullable<O>> {
    try {
      const cache = await this.getCache();
      const response = await cache.match(
        this.getKeyEntitity({ data: dataId, locale: locale }),
      );
      if (!response) return await null;

      const json = (JSON.parse(await response.text()) as E) ?? null;

      return this.getMapper().mapFull(json) ?? (await null);
    } catch (err) {
      console.log('error-load-cache', err);
    }
    return null;
  }

  public async getAllCached({
    locale,
  }: {
    locale: string | null;
  }): Promise<O[] | null> {
    try {
      const cache = await this.getCache();
      const response = await cache.match(this.getKey({ locale: locale }));
      if (!response) return null;

      const json = (JSON.parse(await response.text()) as Array<A>) ?? null;

      return json?.map((item) => this.getMapper().map(item)) ?? (await null);
    } catch (err) {
      console.log('error-load-cache', err);
    }
    return null;
  }

  protected abstract getMapper(): IMapper<EntityKey, A, E, O>;

  protected async setCachedEntities({
    data,
    locale,
  }: {
    data: Array<A>;
    locale: string | null;
  }): Promise<void> {
    try {
      const cache = await this.getCache();
      await cache.put(
        this.getKey({ locale: locale }),
        new Response(JSON.stringify(data)),
      );
    } catch (err) {
      console.log('error-save-cached-entities', err);
    }
  }

  protected async setCachedEntity({
    data,
    locale,
  }: {
    data: E;
    locale: string | null;
  }): Promise<void> {
    try {
      const cache = await this.getCache();
      await cache.put(
        this.getKeyEntitity({
          data: data.id,
          locale: locale,
        }),
        new Response(JSON.stringify(data)),
      );
      await cache.put(
        this.getKeyVersioning({
          data: data.id,
          locale: locale,
        }),
        new Response(JSON.stringify(data.updated_at)),
      );
    } catch (err) {
      console.log('error-save-cached-entity', err);
    }
  }

  protected async removeCachedEntity({
    entityId,
    locale,
  }: {
    entityId: EntityKey;
    locale: string | null;
  }): Promise<boolean> {
    try {
      const cache = await this.getCache();
      return await cache.delete(
        this.getKeyEntitity({ data: entityId, locale: locale }),
      );
    } catch (err) {
      console.log('error-load-army', err);
    }
    return false;
  }

  protected getKey({ locale }: { locale: string | null }): RequestInfo {
    return `/cache/${locale ?? this._locale.getlocale()}${this.key}`;
  }

  protected getKeyVersioning({
    data,
    locale,
  }: {
    data: EntityKey;
    locale: string | null;
  }): RequestInfo {
    return `${this.getKeyEntitity({ locale, data })}/versioning`;
  }

  protected getKeyEntitity({
    data,
    locale,
  }: {
    data: EntityKey;
    locale: string | null;
  }): RequestInfo {
    return `${this.getKey({ locale: locale })}/${data}`;
  }
}
